<template>
  <page content-class="admin-campaign-list">
    <campaign-editor :campaign="campaign" step="gamification">

      <div class="d-flex justify-content-center align-items-middle my-5" v-if="!campaign">
        <b-spinner variant="primary"/>
      </div>

      <b-row v-else class="stages-editor m-0">

        <!-- LEFT SIDE -->
        <b-col md="3" class="border-right p-0">
          <div class="d-flex flex-column justify-content-between h-100">
            <div class="d-flex justify-content-center p-3 border-bottom">
              <span>Start</span>
            </div>
            <div class="d-flex flex-column justify-content-start align-items-center flex-fill py-2 px-2">
              <div class="border vertical-line"></div>
              <b-card
                v-for="component in (components || []).filter(({ leaderboard, collectiveGoal }) => leaderboard || collectiveGoal)"
                :key="component.id"
                class="w-100 mb-2 cursor-pointer"
                @click="changeComponent(component)"
                :style="{
                  background: activeComponent && component.id === activeComponent.id ? (branding ? branding.colour : '#5f27cd') : '#fff',
                  color: activeComponent && component.id === activeComponent.id ? '#fff !important' : 'inherit',
                }"
              >
                <div class="d-flex justify-content-start align-items-center">
                  <div class="pr-2" :style="{width: '36px', fontSize: '24px' }">
                    <font-awesome-icon :icon="componentInfo(component).icon" />
                  </div>
                  <div>
                    <span>{{ componentInfo(component).label }}</span>
                    <br><b-link @click.prevent="deleteComponent($event, component)" :class="{
                      'text-danger': !(activeComponent && component.id === activeComponent.id),
                      'text-white': activeComponent && component.id === activeComponent.id
                    }" size="sm">Delete</b-link>
                  </div>
                </div>
              </b-card>
              <span
                v-if="allowedComponents.length"
                class="plus-button"
                :style="{ background: branding ? branding.colour : '#5f27cd'}"
                @click="activeComponent = null"
              >
                <font-awesome-icon icon="plus"/>
              </span>
            </div>
            <div class="d-flex justify-content-center p-3 border-top">
              <span>End</span>
            </div>
          </div>
        </b-col>

        <b-col md="9" class="p-0 d-flex flex-column justify-content-between">
          <div>
            <b-row class="m-0 pb-4 pr-4">

              <!-- COMPONENT -->
              <template v-if="activeComponent === null && allowedComponents.length">
                <b-col class="pt-4 pl-4" cols="12">
                  <h2>Gamification</h2>
                  <p>Setup the gamification aspect of your campaign</p>
                </b-col>
                <b-col v-for="{ key, label, icon, status, description } in allowedComponents" :key="key" cols="12" class="pb-0 pr-0 pl-4 pt-4">
                  <b-card class="mb-1">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h4><strong><span class="mr-3 text-muted"><font-awesome-icon :icon="icon" /></span>{{ label }}</strong></h4>
                        <p class="text-muted mb-0">{{ description }}</p>
                      </div>
                      <b-button v-if="status === 'ACTIVE'" variant="outline-primary px-3" size="sm" @click="createComponent(key)">+</b-button>
                      <span v-else class="text-secondary" style="font-size: 13px">Coming Soon</span>
                    </div>
                  </b-card>
                </b-col>
              </template>
              <template v-else-if="activeComponent === null && !allowedComponents.length">
                <b-col class="p-5 text-center">
                  <h2>🎉</h2>
                  <h2>Hooray!</h2>
                  <p>You have selected both a leaderboard and a collective goal.<br>
                    Select the one you want to edit on the left hand side.</p>
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'employee-leaderboard'">
                <b-col md="12" class="p-4">
                  <update-leaderboard :value="activeComponent" @input="updateComponent($event)" />
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'raffle-leaderboard'">
                <b-col md="12" class="p-4">
                  <update-leaderboard :value="activeComponent" @input="updateComponent($event)" />
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'collective-goal'">
                <b-col md="12" class="p-4">
                  <update-collective-goal
                    v-if="activeComponent.collectiveGoal && Array.isArray(activeComponent.collectiveGoal.units)"
                    :value="activeComponent"
                    @input="updateComponent($event)"
                  />
                  <update-simplified-collective-goal
                    v-else
                    :value="activeComponent"
                    @input="updateComponent($event)"
                  />
                </b-col>
              </template>

            </b-row>
          </div>
          <div class="d-flex justify-content-end align-items-center px-3 py-2 border-top">
            <b-button
              class="px-3"
              variant="outline-primary"
              @click="$router.push({ name: 'EditCampaignEngagement', params: { campaignID: campaign.id } })"
            >Next</b-button>
          </div>
        </b-col>
      </b-row>
    </campaign-editor>
  </page>
</template>

<script>
import gql from 'graphql-tag';
import CampaignEditor from '@/components/campaign-admin/campaign-editor-frame';
import CampaignAdminStagesQuery from '@/graphql/queries/CampaignAdminStages.gql';
import CreateCampaignComponentMutation from '@/graphql/mutations/CreateCampaignComponent.gql';
import UpdateCampaignComponentMutation from '@/graphql/mutations/UpdateCampaignComponent.gql';
import Page from '@/components/campaign-admin/page';
import UpdateLeaderboard from '@/components/campaign-admin/update-leaderboard';
import UpdateCollectiveGoal from '@/components/campaign-admin/update-collective-goal.vue';
import UpdateSimplifiedCollectiveGoal from '@/components/campaign-admin/update-simplified-collective-goal.vue';
import { mapRoot } from '@src/mixins/root';

const UpdateEventMutation = gql`
  mutation UpdateEvent($update: UpdateEventInput!, $where: EventWhereOneInput!) {
    event: updateEvent(update: $update, where: $where) {
      id
    }
  }
`;

const DeleteCampaignComponentMutation = gql`
  mutation CreateEvent($id: ID!, $campaignID: ID!) {
    deleteCampaignComponent(id: $id, campaign: { id: $campaignID })
  }
`;

const allowedComponents = [ {
  key: 'employee-leaderboard',
  label: 'Points Based Leaderboard',
  description: 'Users can compete eachother by collecting points, once the leaderboard is over the first three are rewarded',
  icon: 'trophy',
  status: 'ACTIVE',
}, {
  key: 'raffle-leaderboard',
  label: 'Raffle Leaderboard',
  description: 'Users can collect tickets, once the leaderboard is finished you can draw winners',
  icon: 'tag',
  status: 'ACTIVE',
}, {
  key: 'collective-goal',
  label: 'Collective Goal',
  description: 'Users can work together to reach a collective goal, this can be planting trees or collecting for a charity',
  icon: 'bullseye',
  status: 'ACTIVE',
} ];

const scores = [
  {
    id: 'shares',
    icon: 'link',
    label: 'Shares',
    value: 1,
    eventNames: [ 'JOB_SHARED', 'CONTENT_SHARED', 'JOB_BROWSE_LINKEDIN', 'JOB_SCHEDULE_SHARE_FIRST', 'CONTENT_SCHEDULE_SHARE_FIRST', 'EVENT_REFERRAL_CREATED', 'REFERRAL_SUGGESTED' ],
    maximum: 5,
  },
  {
    id: 'applications',
    icon: 'file-alt',
    label: 'Applications',
    value: 100,
    eventNames: [ 'APPLICATION_CREATED' ],
  },
  {
    id: 'interviews',
    icon: 'users',
    label: 'Interviews',
    value: 300,
    eventNames: [ 'APPLICATION_INTERVIEW' ],
  },
  {
    id: 'hires',
    icon: 'handshake',
    label: 'Hires',
    value: 1000,
    eventNames: [ 'APPLICATION_HIRED' ],
  }
];

export default {
  name: 'CampaignAdminStages',
  components: {
    Page,
    CampaignEditor,
    UpdateLeaderboard,
    UpdateCollectiveGoal,
    UpdateSimplifiedCollectiveGoal,
  },
  props: {
    campaignID: {
      type: String,
    },
  },
  data() {
    return {
      activeComponent: null,
    };
  },
  apollo: {
    campaign: {
      query: CampaignAdminStagesQuery,
      variables() {
        const { campaignID } = this;
        return { campaignID };
      },
      update({ company }) {
        const { campaign } = company;
        return campaign;
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: 'Failed to load the campaigns' });
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'user' ]),
    allowedComponents() {
      const { isCollectiveGoal, isLeaderboard } = this;
      return allowedComponents
        .filter(({ key }) => {
          if (key === 'collective-goal' && isCollectiveGoal) return false;
          if (key === 'employee-leaderboard' && isLeaderboard) return false;
          if (key === 'raffle-leaderboard' && isLeaderboard) return false;
          return true;
        });
    },
    components() {
      const { campaign } = this;
      if (!campaign) return [];
      return campaign.components;
    },
    branding() {
      const { campaign } = this;
      if (!campaign) return {};
      return campaign.branding;
    },
    isCollectiveGoal() {
      const { components } = this;
      return (components || []).some(({ collectiveGoal }) => collectiveGoal);
    },
    isLeaderboard() {
      const { components } = this;
      return (components || []).some(({ leaderboard }) => leaderboard);
    },
    eventIDs() {
      const { components } = this;
      const events = (components || []).filter(({ event }) => event);
      return events.map(({ event }) => event.id);
    },
  },
  methods: {
    changeComponent(component) {
      this.activeComponent = component;
    },
    async createComponent(key) {
      const component = allowedComponents.find(({ key: k }) => k === key);
      if (!component) return;
      const { campaignID: id } = this;

      const create = {};
      if (key === 'employee-leaderboard') {
        create.leaderboard = {
          type: 'USERS',
          raffleMode: false,
          scores,
        };
      } else if (key === 'raffle-leaderboard') {
        create.leaderboard = {
          type: 'USERS',
          raffleMode: true,
          scores,
        };
      } else if (key === 'collective-goal') {
        create.collectiveGoal = {
          status: 'ACTIVE',
        };
      }
      console.log({ create });
      try {
        const { campaignID } = this;
        await this.$apollo.mutate({
          mutation: CreateCampaignComponentMutation,
          variables: {
            campaign: { id },
            create,
          },
          update(store, { data: { createCampaignComponent } }) {
            const { company } = store.readQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID }
            });
            if (company.campaign && Array.isArray(company.campaign.components)) {
              company.campaign.components.push(createCampaignComponent);
            } else {
              company.campaign.components = [ createCampaignComponent ];
            }
            company.campaign.components.map(el => {
              if (el.leaderboard) {
                el.leaderboard.__typename = 'Leaderboard';
              }
              return el;
            });
            store.writeQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID },
              data: {
                company
              }
            });
          },
        });
        this.$notification({ message: 'Component is successfully added', variant: 'success' });
      } catch (error) {
        this.$notifyErr(error, { prepend: 'Failed to save component' });
        console.log(error);
      }
    },
    async updateComponent(update) {
      const { campaignID: id, activeComponent } = this;
      try {
        if (update.leaderboard) {
          update.leaderboard = {
            type: 'USERS',
            ...update.leaderboard,
          };
        }
        await this.$apollo.mutate({
          mutation: UpdateCampaignComponentMutation,
          variables: {
            campaign: { id },
            componentID: activeComponent.id,
            update,
          }
        });
        await this.updateEvents(update);
        this.$notification({ message: 'Component is successfully updated', variant: 'success' });
      } catch (error) {
        this.$notifyErr(error, { prepend: 'Failed to update component' });
        console.log(error);
      }
    },
    async deleteComponent(event, component) {
      try {
        event.stopPropagation();
        const { id } = component;
        const { campaignID } = this;
        this.activeComponent = null;
        await this.$apollo.mutate({
          mutation: DeleteCampaignComponentMutation,
          variables: { id, campaignID },
          update(store) {
            const { company } = store.readQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID }
            });

            if (company.campaign && Array.isArray(company.campaign.components)) {
              const index = company.campaign.components.findIndex(({ id: componentID }) => id === componentID);
              company.campaign.components.splice(index, 1);
            }
            store.writeQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID: id },
              data: {
                company
              }
            });
          },
        });
      } catch (error) {
        console.log(error);
      }
    },
    componentInfo(component) {
      const { leaderboard, collectiveGoal } = component;
      if (leaderboard && leaderboard.type === 'USERS' && !leaderboard.raffleMode) {
        const { key, label, icon } = allowedComponents.find(({ key: k }) => k === 'employee-leaderboard');
        return { key, label, icon };
      } else if (leaderboard && leaderboard.type === 'USERS' && leaderboard.raffleMode) {
        const { key, label, icon } = allowedComponents.find(({ key: k }) => k === 'raffle-leaderboard');
        return { key, label, icon };
      } else if (collectiveGoal) {
        const { key, label, icon } = allowedComponents.find(({ key: k }) => k === 'collective-goal');
        return { key, label, icon };
      }
      return {};
    },
    async updateEvents(update) {
      const { eventIDs } = this;
      if (!Array.isArray(eventIDs) || !eventIDs.length) return;
      if (update.leaderboard) {
        const { type, ...rest } = update.leaderboard;
        update.leaderboard = rest;
      }
      try {
        for (const eventID of eventIDs) {
          await this.$apollo.mutate({
            mutation: UpdateEventMutation,
            variables: {
              where: { id: eventID },
              update,
            }
          });
        }
      } catch (error) {
        this.$notifyErr(error, { prepend: 'Failed to update referral jam' });
        console.log(error);
      }
    },
  },
};
</script>

<style lang="scss">
.stages-editor {
  .vertical-line {
    width: 0px;
    position: absolute;
    top: 54px;
    bottom: 54px;
    z-index: 0;
  }
  .plus-button {
    display: block;
    width: 36px;
    line-height: 36px;
    text-align: center;
    color: #fff;
    border-radius: 18px;
    z-index: 1;
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }

    &:active {
      opacity: 0.95;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/campaign-admin/Gamification.vue